/**
 * Canvas viewport maths (SD-2.3, DESIGN.md §4 scale, FR-36 spike result).
 *
 * THE REGISTRATION RULE, which is the whole reason this module exists separately from the
 * component: every layer is drawn in the SAME coordinate system, local ENU metres, and the
 * viewport is applied ONCE as a transform on a single wrapping group. Layers cannot drift out of
 * registration under pan or zoom because nothing is positioned per-layer. The alternative, giving
 * each layer its own transform, is the version that eventually disagrees by a pixel at some zoom
 * and is very hard to see in review.
 *
 * SCALE. A 1.7 m module renders 46 px, so the canvas scale is 27 px per metre (DESIGN.md §4). Any
 * scale bar must agree with that; an earlier mockup shipped a bar claiming 66 px was 5 m, which
 * was off by roughly half, so `scaleBar()` derives its pixel width from the same constant rather
 * than carrying its own number.
 *
 * ENU has y pointing NORTH; SVG has y pointing DOWN. The flip happens in exactly one place,
 * `enuToSvg`, so no consumer has to remember it.
 */

/** DESIGN.md §4. A 1.7 m module renders 46 px. */
export const PX_PER_M = 27;

export interface Point2 {
  x: number;
  y: number;
}

export interface BoundsM {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface Viewport {
  /** Multiplier on top of PX_PER_M. 1 means "27 px per metre". */
  zoom: number;
  /** Pan, in SVG user units, applied after scaling. */
  panX: number;
  panY: number;
}

export const IDENTITY_VIEWPORT: Viewport = Object.freeze({ zoom: 1, panX: 0, panY: 0 });

export const MIN_ZOOM = 0.25;
export const MAX_ZOOM = 8;

export const clampZoom = (zoom: number): number =>
  Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));

/** ENU metres to SVG user units. The single place the north/down flip happens. */
export function enuToSvg(point: readonly [number, number] | Point2): Point2 {
  const x = Array.isArray(point) ? point[0] : (point as Point2).x;
  const y = Array.isArray(point) ? point[1] : (point as Point2).y;
  return { x: x * PX_PER_M, y: -y * PX_PER_M };
}

/** SVG user units back to ENU metres. Exact inverse of enuToSvg. */
export function svgToEnu(point: Point2): Point2 {
  return { x: point.x / PX_PER_M, y: -point.y / PX_PER_M };
}

/** Bounding box of every polygon, in metres. Returns null when there is nothing to bound. */
export function boundsOfPolygons(
  polygons: ReadonlyArray<ReadonlyArray<readonly [number, number]>>
): BoundsM | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let seen = 0;

  for (const poly of polygons) {
    for (const [x, y] of poly) {
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      seen += 1;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  return seen === 0 ? null : { minX, minY, maxX, maxY };
}

/** Union of two bounds, so imagery extent and roof extent can be framed together. */
export function unionBounds(a: BoundsM | null, b: BoundsM | null): BoundsM | null {
  if (!a) return b;
  if (!b) return a;
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
  };
}

/** `[min_x, min_y, max_x, max_y]` as a raster reference carries it (roof_model_v1). */
export function boundsFromRasterRef(
  bounds: readonly number[] | null | undefined
): BoundsM | null {
  if (!bounds || bounds.length !== 4) return null;
  const [minX, minY, maxX, maxY] = bounds;
  if (![minX, minY, maxX, maxY].every(Number.isFinite)) return null;
  return { minX, minY, maxX, maxY };
}

export interface ViewBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * The SVG viewBox that frames `bounds` at PX_PER_M, with a margin in METRES so the padding is a
 * real-world distance rather than a pixel value that changes meaning with zoom.
 */
export function fitViewBox(bounds: BoundsM | null, marginM = 2): ViewBox {
  if (!bounds) {
    // A square metre at the origin. Renders nothing, but keeps the SVG valid rather than emitting
    // a zero or NaN viewBox, which browsers handle inconsistently.
    return { x: 0, y: 0, width: PX_PER_M, height: PX_PER_M };
  }
  const topLeft = enuToSvg([bounds.minX - marginM, bounds.maxY + marginM]);
  const bottomRight = enuToSvg([bounds.maxX + marginM, bounds.minY - marginM]);
  return {
    x: topLeft.x,
    y: topLeft.y,
    width: Math.max(bottomRight.x - topLeft.x, 1),
    height: Math.max(bottomRight.y - topLeft.y, 1),
  };
}

export const viewBoxAttr = (v: ViewBox): string => `${v.x} ${v.y} ${v.width} ${v.height}`;

/**
 * The transform for the ONE group that wraps every layer.
 *
 * Order is translate-then-scale, so `panX`/`panY` are in final SVG units and a drag of N units
 * moves the scene by N units at any zoom. Scaling first would make pan speed depend on zoom, which
 * feels like the canvas is fighting the pointer.
 */
export function viewportTransform(viewport: Viewport): string {
  const { zoom, panX, panY } = viewport;
  return `translate(${panX} ${panY}) scale(${zoom})`;
}

/** Apply a viewport to a point, for hit-testing. Inverse of `unproject`. */
export function project(pointSvg: Point2, viewport: Viewport): Point2 {
  return {
    x: pointSvg.x * viewport.zoom + viewport.panX,
    y: pointSvg.y * viewport.zoom + viewport.panY,
  };
}

/** Undo a viewport. Every interaction derives scene coordinates through this, never through a
 * measured element width, which is UX-DR10's first regression guard. */
export function unproject(pointScreen: Point2, viewport: Viewport): Point2 {
  return {
    x: (pointScreen.x - viewport.panX) / viewport.zoom,
    y: (pointScreen.y - viewport.panY) / viewport.zoom,
  };
}

/** Zoom about a fixed point, so the scene position under the cursor stays under the cursor. */
export function zoomAbout(viewport: Viewport, focusScreen: Point2, factor: number): Viewport {
  const nextZoom = clampZoom(viewport.zoom * factor);
  // Solve for the pan that keeps `focusScreen` mapping to the same scene point.
  const scene = unproject(focusScreen, viewport);
  return {
    zoom: nextZoom,
    panX: focusScreen.x - scene.x * nextZoom,
    panY: focusScreen.y - scene.y * nextZoom,
  };
}

export interface ScaleBar {
  /** The round number of metres the bar represents. */
  metres: number;
  /** Its on-screen width in pixels at the given zoom. */
  px: number;
  label: string;
}

/**
 * Sub-metre steps are included because at MAX_ZOOM one metre is 216 px, so a bar limited to 1 m
 * would overflow its own maximum width. A 0.25 m bar at 8x is both correct and useful; a 1 m bar
 * that runs off the chrome is neither.
 */
const NICE_METRES = [0.25, 0.5, 1, 2, 5, 10, 20, 50, 100] as const;

/**
 * A scale bar whose pixel width is DERIVED from PX_PER_M rather than asserted independently.
 * Picks the largest round distance that fits `maxPx`, falling back to the smallest step so the bar
 * never disappears at extreme zoom.
 */
export function scaleBar(viewport: Viewport, maxPx = 120): ScaleBar {
  const pxPerMetre = PX_PER_M * viewport.zoom;
  // Annotated as `number`, not inferred: `NICE_METRES[0]` narrows to the literal 0.25 and the
  // loop below then cannot assign any other step to it.
  let chosen: number = NICE_METRES[0];
  for (const m of NICE_METRES) {
    if (m * pxPerMetre <= maxPx) chosen = m;
  }
  return {
    metres: chosen,
    px: chosen * pxPerMetre,
    label: `${chosen} m`,
  };
}
